import { useCallback, useEffect, useMemo, useState } from "react";
import { BackupFileItem } from "../../view/contents/backup/backup.file.list";
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";
import TableCheckBox from "../common/table.checkbox";
import { useForm } from "react-hook-form";
import { TableContainer } from "../../style/contents/backup/backup";
import Pagination from "../common/table.pagination";
import {
  fetchRequestDownloadFile,
  fetchRequestWormDirectory,
  fetchRequestWormFile,
} from "../../api/admin";
import ModalCenterLayout from "../modal/modal.center.layout";

interface FileDataProps {
  data: BackupFileItem[];
  pageSize: number;
  onFolderClick?: (path: string) => void; // ✅ 폴더 클릭 시 전달받을 함수
  id?: number;
  deviceId: string;
  isLoading?: boolean; // ✅ 추가
  role: string;
  loginToken?: string;
}
export interface PaginationState {
  pageIndex: number;
  pageSize: number;
}

const FileList = ({
  data,
  pageSize,
  onFolderClick,
  id,
  deviceId,
  isLoading,
  role,
  loginToken = "",
}: FileDataProps) => {
  const { handleSubmit } = useForm();
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize,
  });
  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});
  const [wormTargetItem, setWormTargetItem] =
    useState<BackupFileItem | null>(null);
  const [wormModal, setWormModal] = useState({
    isOpen: false,
    isVisible: false,
  });

  console.log("filelist", data);

  const handleOpenWormModal = useCallback((file: BackupFileItem) => {
    setWormTargetItem(file);
    setWormModal({ isOpen: true, isVisible: true });
  }, []);

  const handleCloseWormModal = useCallback(() => {
    setWormTargetItem(null);
    setWormModal({ isOpen: false, isVisible: false });
  }, []);

  const handleRequestWorm = useCallback(async () => {
    if (!id || !loginToken) {
      alert("사용자 또는 로그인 토큰 정보가 없습니다.");
      return;
    }

    if (!wormTargetItem) {
      alert("WORM 요청할 항목 정보가 없습니다.");
      return;
    }

    try {
      if (wormTargetItem.fileType === "d") {
        if (!wormTargetItem.path) {
          alert("WORM 요청할 폴더 경로 정보가 없습니다.");
          return;
        }

        const params = {
          deviceId,
          directoryPath: wormTargetItem.path,
        };

        console.log("[BackupFileList WORM] directory request params", {
          userId: id,
          ...params,
        });

        await fetchRequestWormDirectory(String(id), params, loginToken);
      } else {
        if (
          wormTargetItem.fileId == null ||
          wormTargetItem.backupHistoryId == null
        ) {
          alert("WORM 요청할 파일 정보가 없습니다.");
          return;
        }

        const params = {
          fileId: wormTargetItem.fileId,
          backupHistoryId: wormTargetItem.backupHistoryId,
        };

        console.log("[BackupFileList WORM] file request params", {
          userId: id,
          ...params,
        });

        await fetchRequestWormFile(String(id), params, loginToken);
      }

      alert("WORM 요청이 접수되었습니다.");
      handleCloseWormModal();
    } catch (err) {
      console.error("WORM 요청 실패:", err);
      alert("WORM 요청 중 오류가 발생했습니다.");
    }
  }, [deviceId, handleCloseWormModal, id, loginToken, wormTargetItem]);

  const columns = useMemo<ColumnDef<BackupFileItem>[]>(
    () => [
      ...(role === "admin"
        ? [
            {
              id: "select",
              header: "선택",
              cell: ({ row }: any) => (
                <div className="px-1">
                  <TableCheckBox
                    {...{
                      checked: row.getIsSelected(),
                      disabled: !row.getCanSelect(),
                      indeterminate: row.getIsSomeSelected(),
                      onChange: row.getToggleSelectedHandler(),
                      onClick: (e) => e.stopPropagation(),
                    }}
                  />
                </div>
              ),
              size: 50,
            },
          ]
        : []),
      // {
      //   header: "No.",
      //   id: "number", // 넘버링을 위한 id
      //   // cell: ({ row }) => row.index + 1, // 각 행의 인덱스에 1을 더해 넘버링
      //   cell: ({ row }: { row: Row<BackupFileItem> }) => {
      //     const totalCount = data.length; // totalRowCount = 전체 데이터 수
      //     if (totalCount === 0) return "-";

      //     const globalIndex = row.index; // 이미 글로벌 인덱스임
      //     return totalCount - globalIndex;
      //   },
      //   size: 60,
      // },
      {
        header: "종류",
        accessorKey: "fileType",
        cell: (info) => {
          const type = info.getValue();
          if (type === "f") return "파일";
          if (type === "d")
            return (
              <div className="folder-icon">
                <img src="/assets/1f4c2.png" alt="" />
                <span>폴더</span>
              </div>
            );
          return "-";
        },
        size: 120,
      },
      {
        header: "이름",
        accessorKey: "fileName",
        cell: (info) => info.getValue(),
        size: 400,
      },
      {
        header: "백업날짜",
        accessorKey: "createDate",
        cell: (info) => (info.getValue() ? info.getValue() : "-"),
        size: 200,
      },
      ...(role === "admin"
        ? [
            {
              header: "WORM",
              id: "worm",
              cell: ({ row }: any) => {
                const item = row.original as BackupFileItem;

                if (item.isWorm) return "적용됨";

                return (
                  <button
                    type="button"
                    className="worm-request-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpenWormModal(item);
                    }}
                  >
                    WORM
                  </button>
                );
              },
              size: 120,
            },
          ]
        : []),
    ],
    [role, handleOpenWormModal],
  );
  const table = useReactTable({
    data,
    columns,
    state: {
      rowSelection,
      pagination,
    },
    enableRowSelection: true,
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: setPagination,
  });

  const selectedRowCount = table.getSelectedRowModel().rows.length;
  const totalRowCount = table.getRowCount();

  const onSubmit = async () => {
    const selectedRows = table.getSelectedRowModel().rows;
    const selectedItems = selectedRows.map((row) => row.original);

    if (selectedItems.length === 0) {
      alert("압축할 항목을 선택해주세요.");
      return;
    }

    const adminUserId = localStorage.getItem("userId");
    const targetUserId = id;
    const targetDeviceId = deviceId;

    if (!adminUserId || !targetUserId || !targetDeviceId) {
      alert("사용자 정보가 누락되었습니다.");
      return;
    }

    let successCount = 0;
    let failCount = 0;

    for (const item of selectedItems) {
      try {
        const res = await fetchRequestDownloadFile(
          adminUserId,
          item.path,
          String(targetUserId),
          targetDeviceId,
        );
        console.log(`✅ 압축 요청 성공: ${item.path}`);
        console.log(res);
        successCount++;
      } catch (err) {
        console.error(`❌ 압축 요청 실패: ${item.path}`, err);
        failCount++;
      }
    }

    alert(`압축 요청 완료\n성공: ${successCount}개\n실패: ${failCount}개`);
  };

  const handleRowClick = (rowData: BackupFileItem) => {
    console.log("Clicked row data:", rowData);
    // 여기에 클릭 시 실행할 동작을 정의하세요.
    // 예: rowData에 대한 상세 정보 보기, 수정하기, 모달 열기 등
  };

  useEffect(() => {
    if (data.length > 0) {
      table.setPageSize(pageSize);
    }
  }, [table, pageSize, data]);

  return (
    <TableContainer>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="table-head-wrap">
          <div className="left">
            <span className="table-total-row">
              {role === "admin" && (
                <TableCheckBox
                  {...{
                    checked: table.getIsAllRowsSelected(),
                    indeterminate: table.getIsSomePageRowsSelected(),
                    onChange: table.getToggleAllRowsSelectedHandler(),
                  }}
                />
              )}
              {role === "admin"
                ? `전체 선택 (${selectedRowCount} / ${totalRowCount})`
                : `전체 ${totalRowCount}`}
            </span>
          </div>
          {role === "admin" && (
            <div className="right">
              <div className="btn-box">
                <button type="submit" className="compress">
                  압축하기(zip 파일)
                </button>
              </div>
            </div>
          )}
        </div>
        {isLoading ? (
          <div className="loading">데이터를 불러오는 중입니다...</div>
        ) : (
          <div className="fixed-height" style={{ maxHeight: "36rem" }}>
            <table>
              <thead>
                {table.getHeaderGroups().map((item) => (
                  <tr key={item.id}>
                    {item.headers.map((header) => (
                      <th
                        key={header.id}
                        style={{ width: `${header.getSize()}px` }}
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.length === 0 ? (
                  <tr>
                    <td colSpan={columns.length} className="no-data-row">
                      🔍 검색 결과가 없습니다.
                    </td>
                  </tr>
                ) : (
                  table.getRowModel().rows.map((row) => (
                    <tr
                      key={row.id}
                      onClick={() => handleRowClick(row.original)}
                    >
                      {row.getVisibleCells().map((cell) => {
                        const columnId = cell.column.id;
                        const fileType = row.original.fileType;
                        const isFileTypeCell = columnId === "fileType";
                        const isFileNameCell = columnId === "fileName";

                        let className = "";

                        if (isFileTypeCell) {
                          className =
                            fileType === "d" ? "type-folder" : "type-file";
                        }

                        if (isFileNameCell) {
                          className =
                            fileType === "d"
                              ? "class-folder-name"
                              : "class-file-name";
                          const value = cell.getValue();

                          return (
                            <td key={cell.id} className={className}>
                              {fileType === "d" ? (
                                <span
                                  className="clickable-folder"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onFolderClick?.(row.original.path);
                                  }}
                                >
                                  {typeof value === "string"
                                    ? value
                                    : String(value)}
                                </span>
                              ) : (
                                <span>
                                  {typeof value === "string"
                                    ? value
                                    : String(value)}
                                </span>
                              )}
                            </td>
                          );
                        }

                        return (
                          <td key={cell.id} className={className}>
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext(),
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
        {table.getPageCount() > 0 && (
          <Pagination
            page={pagination.pageIndex}
            pages={table.getPageCount()}
            onPageChange={(page) => {
              table.setPageIndex(page);
            }}
          />
        )}
        {wormModal.isOpen && (
          <ModalCenterLayout
            isVisible={wormModal.isVisible}
            closedHandler={handleCloseWormModal}
            size="s"
          >
            <div className="worm-confirm-modal">
              <div className="modal-header">
                <h3>WORM 기능 적용</h3>
                <button
                  type="button"
                  className="closed-modal-btn"
                  onClick={handleCloseWormModal}
                >
                  ×
                </button>
              </div>
              <div className="modal-body">
                <p>
                  적용하시겠습니까? worm 기능이 적용되면 이미 저장된 데이터의
                  수정/삭제가 불가능하게 됩니다.
                </p>
              </div>
              <div className="modal-actions">
                <button
                  type="button"
                  className="cancel"
                  onClick={handleCloseWormModal}
                >
                  취소
                </button>
                <button
                  type="button"
                  className="confirm"
                  onClick={handleRequestWorm}
                >
                  적용
                </button>
              </div>
            </div>
          </ModalCenterLayout>
        )}
      </form>
    </TableContainer>
  );
};

export default FileList;
